import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { useEventFiltersStore } from "../../stores/event-filters-store";
import {
  formatEventDateTime,
  formatEventPrice,
  getBookingStatus,
} from "../../lib/utils";
import type { Event } from "../../lib/types";
import { buildEventImageAttributes } from "../../lib/event-image-urls";

interface EventCardProps {
  event: Event;
}

export const EventCard = ({ event }: EventCardProps) => {
  const { selectedTags, setSelectedTags } = useEventFiltersStore();

  const eventDateTime = formatEventDateTime(
    event.startsAt,
    event.endsAt,
    event.sessions,
  );

  const getTagVariant = () => {
    // Always use default variant for consistent styling
    return "default" as const;
  };

  const handleTagClick = (tagName: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (selectedTags.includes(tagName)) {
      setSelectedTags(selectedTags.filter((name) => name !== tagName));
    } else {
      setSelectedTags([...selectedTags, tagName]);
    }
  };

  const img = buildEventImageAttributes(event.imageUrl, "card");

  const bookingCapacityStatus = getBookingStatus(
    event.pricePoints,
    event.bookingClosesAt,
    event.startsAt,
  );

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full p-0">
      <div className="relative flex-shrink-0">
        <img
          src={img.src}
          srcSet={img.srcSet}
          sizes={img.sizes}
          width={img.width}
          height={192}
          alt={event.title}
          className="w-full h-48 object-cover"
          decoding="async"
        />
        <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-2 items-center">
          {event.isCancelled && (
            <Badge variant="error" size="sm" className="shrink-0">
              Cancelled
            </Badge>
          )}
          {!event.isCancelled && bookingCapacityStatus === "limited" && (
            <Badge variant="warning" size="sm" className="shrink-0">
              Limited Places Remaining
            </Badge>
          )}
          {!event.isCancelled && bookingCapacityStatus === "full" && (
            <Badge variant="error" size="sm" className="shrink-0">
              Fully Booked
            </Badge>
          )}
          {(event.tags as string[])?.map((tagName) => {
            const isActive = selectedTags.includes(tagName);
            return (
              <Badge
                key={tagName}
                variant={getTagVariant()}
                size="sm"
                className={`shrink-0 cursor-pointer hover:opacity-80 transition-all ${
                  isActive ? "ring-2 ring-white shadow-lg" : ""
                }`}
                onClick={(e) => handleTagClick(tagName, e)}
                title={
                  isActive
                    ? `Filtering by ${tagName} - click to remove`
                    : `Filter by ${tagName}`
                }
              >
                {tagName}
                {isActive && " ✓"}
              </Badge>
            );
          })}
        </div>
      </div>

      <CardHeader className="px-6 pt-6">
        <CardTitle className="text-xl">{event.title}</CardTitle>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-4 px-6 pb-6">
        <div className="flex items-center text-sm text-gray-600">
          <Calendar className="w-4 h-4 mr-2" />
          <span>{eventDateTime.date}</span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <Clock className="w-4 h-4 mr-2" />
          <span>{eventDateTime.time}</span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <MapPin className="w-4 h-4 mr-2" />
          <span>
            {(event as { location?: string | null }).location || "Sailing Club"}
          </span>
        </div>

        <p className="text-gray-700 line-clamp-2">{event.description}</p>

        <div className="flex justify-between items-end gap-4 pt-4 mt-auto">
          <div className="text-right flex-1 min-w-0">
            {!event.isCancelled && (
              <p className="text-lg font-semibold text-gray-900 break-words leading-tight min-h-[1.5rem]">
                {formatEventPrice(
                  event.pricePoints,
                  event.bookingClosesAt,
                  event.startsAt,
                )}
              </p>
            )}
          </div>
          <Button asChild className="flex-shrink-0">
            <Link to={`/events/${event.id}`}>View details</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
